import NoSsr from "@mui/material/NoSsr";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useItemTooltip, useMouseTracker } from "@mui/x-charts/ChartsTooltip";
import { generateVirtualElement } from "./generateVirtualElement";
import { Box } from "@mui/system";

interface CustomItemTooltipProps {
  dataset?: any[];
  colorMap?: Record<string, string>;
}

export function CustomItemTooltip({ dataset }: CustomItemTooltipProps = {}) {
  const colorMap = (arguments && arguments[0] && (arguments[0] as any).colorMap) || undefined;
  const tooltipData = useItemTooltip();
  const mousePosition = useMouseTracker();

  if (!tooltipData || !mousePosition) {
    return null;
  }

  const isMousePointer = mousePosition?.pointerType === "mouse";
  const yOffset = isMousePointer ? 0 : 40 - mousePosition.height;

  // Check if this is a line series by checking the label - bar series shows "Daily Number of Competitors"
  const isLineTooltip = tooltipData.label !== "Daily Number of Competitors";

  // Get the current data point from the dataset
  // let competitorCount;
  let competitorName: string | undefined;
  let competitorColor: string | undefined;
  let dataPointDate: string | undefined;
  if (dataset && dataset.length > 0 && tooltipData.value !== undefined) {
    // Find the data point that contains this value
    const currentDataPoint = dataset.find((item) => {
      // For line series, try to match the current value with any competitor value
      if (isLineTooltip) {
        return Object.keys(item).some(
          (key) =>
            key !== "date" &&
            key !== "num_competitors" &&
            item[key] === tooltipData.value
        );
      }
      return false;
    });

    if (currentDataPoint) {
      // competitorCount = currentDataPoint.num_competitors;
      dataPointDate = currentDataPoint.date;

      const matchedKey = Object.keys(currentDataPoint).find(
        (key) => key !== "date" && key !== "num_competitors" && currentDataPoint[key] === tooltipData.value
      );

      if (matchedKey) {
        competitorName = matchedKey;

        if (colorMap && colorMap[matchedKey]) {
          competitorColor = colorMap[matchedKey];
        } else {
          const hash = Array.from(matchedKey).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
          const hue = hash % 360;
          competitorColor = `hsl(${hue}, 80%, 60%)`;
        }
      }
    }
  }

  return (
    <NoSsr>
      <Popper
        sx={{
          pointerEvents: "none",
          zIndex: (theme) => theme.zIndex.modal,
        }}
        open
        placement={isMousePointer ? "bottom" : "top"}
        anchorEl={generateVirtualElement(mousePosition)}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, yOffset],
            },
          },
        ]}
      >
        <Paper
          elevation={0}
          sx={{
            m: 1,
            p: 1.5,
            border: "solid",
            borderWidth: 0,
            borderColor: "divider",
            boxShadow: "0px 0px 3px #00000073",
            position: "relative",
          }}
        >
          <Stack
            direction="column"
            alignItems="flex-start"
            sx={{ paddingLeft: "0px", paddingRight: "0px" }}
          >
            {/* Show tracked metric name and value */}
            <Typography 
              fontWeight={500} 
              fontSize="10px" 
              sx={{ 
              lineHeight: "14px", 
              letterSpacing: "0px", 
              verticalAlign: "middle" 
              }}
            >
              IMP SHARE: {tooltipData.formattedValue} %
            </Typography>

            {/* If we identified a competitor (series key), show it with a colored marker */}
            {isLineTooltip && competitorName && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                <Box
                  sx={{
                  width: 12,
                  height: 4,
                  backgroundColor: competitorColor || "#000",
                  display: "inline-block",
                  }}
                />
                <Typography 
                  fontWeight={400} 
                  fontSize="10px" 
                  sx={{ 
                  color: "#666", 
                  lineHeight: "14px", 
                  letterSpacing: "0.15px", 
                  verticalAlign: "middle" 
                  }}
                >
                  {competitorName === 'You' ? 'Optival' : competitorName}
                </Typography>
                </Stack>
            )}

            {/* {isLineTooltip && competitorCount !== undefined && (
              <Typography
                fontWeight="light"
                fontSize={14}
                sx={{ color: "#666", mt: 0.5 }}
              >
                Daily Number of Competitors: {competitorCount}
              </Typography>
            )} */}

            {/* Show formatted date if available */}
            {dataPointDate && (
              <Typography 
              fontWeight={400} 
              fontSize="8px" 
              sx={{ 
                color: "#999", 
                mt: 0.5,
                lineHeight: "12px",
                letterSpacing: "0.15px",
                verticalAlign: "middle"
              }}
              >
              {(() => {
                const d = new Date(dataPointDate!);
                if (!isNaN(d.getTime())) {
                const day = String(d.getDate()).padStart(2, "0");
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const year = d.getFullYear();
                return `${day}/${month}/${year}`;
                }
                return dataPointDate;
              })()}
              </Typography>
            )}
          </Stack>
          <Box
            style={{
              position: "absolute",
              transform: "rotate(90deg)",
              top: "-10px",
              right: "45%",
              zIndex: "1",
              width: 0,
              height: 0,
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderRight: "8px solid #fff",
            }}
          />
        </Paper>
      </Popper>
    </NoSsr>
  );
}
